import { t } from './i18n';

/**
 * Horizon drag mode: a line across the screen the user drags onto the photo's
 * horizon. Reports the screen fraction (0 top .. 1 bottom); the caller maps
 * it to the photo.
 */
export class HorizonTool {
  private readonly overlay: HTMLElement;
  private readonly line: HTMLElement;
  private readonly hint: HTMLElement;
  private active = false;
  private dragging = false;
  private y = 0.5;

  constructor(
    host: HTMLElement,
    private readonly onChange: (screenY: number) => void,
    private readonly onDone: () => void,
  ) {
    this.overlay = document.createElement('div');
    this.overlay.className = 'horizon-overlay';
    this.overlay.hidden = true;
    this.line = document.createElement('div');
    this.line.className = 'horizon-line';
    this.hint = document.createElement('div');
    this.hint.className = 'horizon-hint';
    this.overlay.append(this.line, this.hint);
    host.appendChild(this.overlay);

    this.overlay.addEventListener('pointerdown', (e) => {
      this.dragging = true;
      this.overlay.setPointerCapture(e.pointerId);
      this.moveTo(e.clientY);
    });
    this.overlay.addEventListener('pointermove', (e) => {
      if (this.dragging) this.moveTo(e.clientY);
    });
    const end = (e: PointerEvent) => {
      if (!this.dragging) return;
      this.dragging = false;
      this.moveTo(e.clientY);
    };
    this.overlay.addEventListener('pointerup', end);
    this.overlay.addEventListener('pointercancel', end);
    this.hint.addEventListener('click', (e) => {
      e.stopPropagation();
      this.stop();
    });
    this.hint.addEventListener('pointerdown', (e) => e.stopPropagation());
  }

  get isActive(): boolean {
    return this.active;
  }

  start(screenY: number): void {
    this.active = true;
    this.y = screenY;
    this.overlay.hidden = false;
    this.hint.textContent = t('horizonHint');
    this.layout();
  }

  stop(): void {
    if (!this.active) return;
    this.active = false;
    this.overlay.hidden = true;
    this.onDone();
  }

  /** Keep the line where the horizon is when the window resizes. */
  set(screenY: number): void {
    this.y = screenY;
    this.layout();
  }

  private moveTo(clientY: number): void {
    const h = this.overlay.clientHeight || window.innerHeight;
    this.y = Math.min(0.98, Math.max(0.02, clientY / h));
    this.layout();
    this.onChange(this.y);
  }

  private layout(): void {
    this.line.style.top = `${(this.y * 100).toFixed(3)}%`;
  }
}
